import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, resolveComponent as _resolveComponent, mergeProps as _mergeProps, createVNode as _createVNode, withCtx as _withCtx, vModelText as _vModelText, withKeys as _withKeys, withDirectives as _withDirectives, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "station-form-category-container flex flex-col gap-y-2 pb-6" }
const _hoisted_2 = /*#__PURE__*/_createElementVNode("span", { class: "station-form-category-title font-bold" }, "Observações", -1)
const _hoisted_3 = { class: "flex flex-col gap-3" }
const _hoisted_4 = {
  key: 0,
  class: "text-gray-500 text-sm text-left"
}
const _hoisted_5 = {
  key: 1,
  class: "text-gray-500 text-sm text-left"
}
const _hoisted_6 = { class: "text-start" }
const _hoisted_7 = { class: "font-bold text-sm" }
const _hoisted_8 = { class: "text-xs font-normal text-gray-500" }
const _hoisted_9 = { class: "text-sm mt-1 text-left" }
const _hoisted_10 = { class: "flex border rounded px-3 py-3 gap-4 items-start mt-2" }
const _hoisted_11 = ["onKeyup"]

import { onMounted, ref, computed } from 'vue'
import NetworkService from '@/Network/index'
import { DoctorStationObservationListResponse } from '@/Network/Types/Responses/DsObservationResponse'
import { mdiTrashCanOutline } from '@mdi/js'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import { subHours, format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useAuthStore } from '@/pinia-store/useAuthStore'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'

interface Observation {
  id?: number
  authorId?: number
  text: string
  author?: string
  timestamp: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'observation.form',
  props: {
    doctorStationId: {},
    scheduleId: {},
    timezone: {}
  },
  setup(__props: any) {

const props = __props;



const isLoading = ref(false)
const isLoadingList = ref(false)
const toast = useToast()
const { notifyError } = useErrorToast()
const draftObservation = ref('')
const listObservations = ref<DoctorStationObservationListResponse>({ data: [], count: 0 })
const { getUserId } = useAuthStore()
const permissionsStore = usePermissionsStore()

const canDeleteObservations = computed(() => {
  if (!props.scheduleId) return false
  return permissionsStore.check({
    permission: AuthorizationPermissions.StationObservationsDeleting,
    resource: {
      objectType: AuthorizationResourcesAndSubjects.Schedule,
      objectId: Number(props.scheduleId)
    }
  })
})

const canCreateObservation = computed(() => {
  return permissionsStore.checkAnyRessources({
    permission: AuthorizationPermissions.StationObservationsCreating,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  })
})

function parseOffsetHours(offset: string | undefined): number {
  if (!offset) return 3
  try {
    const hours = parseInt(offset.split(':')[0], 10)
    return Math.abs(hours)
  } catch {
    return 3
  }
}

const localObservations = computed<Observation[]>(() =>
  listObservations.value.data.map((obs) => {
    let formattedTimestamp = ''

    if (obs.createdAt) {
      try {
        const utcDate = new Date(obs.createdAt)
        const hoursToSubtract = parseOffsetHours(props.timezone)
        const localDate = subHours(utcDate, hoursToSubtract)
        formattedTimestamp = format(localDate, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
      } catch (e) {
        console.error('Erro ao formatar data:', e)
        formattedTimestamp = 'Data inválida'
      }
    }

    return {
      id: obs.id,
      author: obs.author?.name ?? 'Usuário',
      authorId: obs.author?.id ?? null,
      text: obs.text,
      timestamp: formattedTimestamp
    }
  })
)

const fetchObservations = async () => {
  if (!props.doctorStationId) return
  try {
    isLoadingList.value = true
    const fetchedObs = await NetworkService.observation.list(Number(props.doctorStationId))
    listObservations.value = fetchedObs
  } catch (error) {
    // Antes: swallow com console.error — a falha de load parecia "sem observações".
    console.error('Erro ao buscar observações:', error)
    notifyError(error, 'Não foi possível carregar as observações')
  } finally {
    isLoadingList.value = false
  }
}

const handleSaveDraft = async () => {
  const text = draftObservation.value.trim()
  if (!text) return

  try {
    isLoading.value = true
    if (props.doctorStationId) {
      const saved = await NetworkService.observation.create(Number(props.doctorStationId), {
        text,
        scheduleId: Number(props.scheduleId)
      })
      listObservations.value.data.unshift(saved)
      listObservations.value.count++
    }
    draftObservation.value = ''
    toast.success('Observação criada com sucesso!')
  } catch (err) {
    // Antes: "Erro ao criar observação!" — a mensagem específica do
    // ServiceError chega intacta ao usuário.
    notifyError(err, 'Não foi possível criar a observação')
    console.error('Erro ao salvar observação:', err)
  } finally {
    isLoading.value = false
  }
}

const handleDelete = async (observation: Observation, index: number) => {
  if (props.doctorStationId && observation.id) {
    try {
      await NetworkService.observation.delete(
        Number(observation.id),
        Number(props.scheduleId),
        Number(observation?.authorId)
      )
      listObservations.value.data.splice(index, 1)
      listObservations.value.count--
      toast.success('Observação removida com sucesso.')
    } catch (error) {
      console.error('Erro ao deletar observação:', error)
      // Antes: "Erro ao deletar observação." estático.
      notifyError(error, 'Não foi possível remover a observação')
      return
    }
  }
}

onMounted(async () => {
  await fetchObservations()
})

return (_ctx: any,_cache: any) => {
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_tooltip = _resolveComponent("v-tooltip")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _hoisted_2,
    _createElementVNode("div", _hoisted_3, [
      (isLoadingList.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, " Carregando observações... "))
        : _createCommentVNode("", true),
      (localObservations.value.length === 0 && !isLoadingList.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, " Nenhuma observação registrada. "))
        : _createCommentVNode("", true),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(localObservations.value, (obs, index) => {
        return (_openBlock(), _createElementBlock("div", {
          key: obs.id || index,
          class: "bg-gray-100 p-3 rounded-lg flex justify-between items-start"
        }, [
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("p", _hoisted_7, [
              _createTextVNode(_toDisplayString(obs.author) + " ", 1),
              _createElementVNode("span", _hoisted_8, " | " + _toDisplayString(obs.timestamp), 1)
            ]),
            _createElementVNode("p", _hoisted_9, _toDisplayString(obs.text), 1)
          ]),
          _createVNode(_component_v_tooltip, { text: "Remover" }, {
            activator: _withCtx(({ props }) => [
              _createVNode(_component_v_btn, _mergeProps(props, {
                onClick: ($event: any) => (handleDelete(obs, index)),
                color: "error",
                variant: "text",
                icon: _unref(mdiTrashCanOutline),
                density: "compact",
                size: "small",
                disabled: !canDeleteObservations.value && _unref(getUserId) != obs.authorId
              }), null, 16, ["onClick", "icon", "disabled"])
            ]),
            _: 2
          }, 1024)
        ]))
      }), 128))
    ]),
    _createElementVNode("div", _hoisted_10, [
      _withDirectives(_createElementVNode("input", {
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((draftObservation).value = $event)),
        type: "text",
        placeholder: "Digite uma observação...",
        class: "flex-grow border-none outline-none text-sm placeholder-gray-500",
        onKeyup: _withKeys(handleSaveDraft, ["enter"])
      }, null, 40, _hoisted_11), [
        [_vModelText, draftObservation.value]
      ]),
      (canCreateObservation.value)
        ? (_openBlock(), _createBlock(FilledButton, {
            key: 0,
            type: "button",
            color: "primary30",
            class: "text-white text-xs font-medium",
            onClick: handleSaveDraft,
            width: "130",
            height: "32",
            loading: isLoading.value,
            disabled: !draftObservation.value
          }, {
            default: _withCtx(() => [
              _createTextVNode(" Nova observação ")
            ]),
            _: 1
          }, 8, ["loading", "disabled"]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})